import { Dialog } from '@avanta/shared/components/ui/Dialog';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { useDialogContext } from '@avanta/shared/contexts/Dialog';
import { usePredefinedPhrasesStore } from '@avanta/shared/stores/chat/predefined-phrases';
import { ChevronRight, Search, TextInitial, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PREDEFINED_PHRASE_ALL_CATEGORY,
  PREDEFINED_PHRASE_CATEGORY_FILTERS,
  PREDEFINED_PHRASE_CATEGORY_LABEL_KEYS,
  PREDEFINED_PHRASE_CATEGORY_ORDER,
  PREDEFINED_PHRASES,
  PREDEFINED_PHRASES_TOTAL_COUNT,
} from 'constants/chat';
import type { PredefinedPhraseCategoryId, PredefinedPhraseContentCategoryId } from 'types/chat';
import './styles.css';
import {
  categoryChipClassName,
  PREDEFINED_PHRASES_DIALOG_CLASSNAME,
  phraseItemClassName,
  SEARCH_INPUT_CLASSNAME,
} from './styles';

const withCount = (label: string, count: number): string => `${label} (${count})`;

export const PredefinedPhrasesModal = (): React.ReactElement => {
  const { hideDialog } = useDialogContext();
  const requestPhraseInsertion = usePredefinedPhrasesStore(state => state.requestPhraseInsertion);
  const { t } = useTranslation(LOCIZE_NS.CHAT);
  const closeLabel = t('Chat.PredefinedPhrases.close');
  const modalTitle = withCount(t('Chat.PredefinedPhrases.title'), PREDEFINED_PHRASES_TOTAL_COUNT);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<PredefinedPhraseCategoryId>(
    PREDEFINED_PHRASE_ALL_CATEGORY
  );

  const filteredPhrases = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const isAllCategories = activeCategory === PREDEFINED_PHRASE_ALL_CATEGORY;

    return PREDEFINED_PHRASES.filter(phrase => {
      if (!isAllCategories && phrase.categoryId !== activeCategory) return false;
      if (!query) return true;
      return t(phrase.messageKey).toLowerCase().includes(query);
    });
  }, [activeCategory, searchQuery, t]);

  const groupedPhrases = useMemo(() => {
    const isAllCategories = activeCategory === PREDEFINED_PHRASE_ALL_CATEGORY;
    const categoriesToShow: PredefinedPhraseContentCategoryId[] = isAllCategories
      ? PREDEFINED_PHRASE_CATEGORY_ORDER
      : [activeCategory];

    return categoriesToShow.flatMap(categoryId => {
      const phrases = filteredPhrases.filter(phrase => phrase.categoryId === categoryId);
      return phrases.length > 0 ? [{ categoryId, phrases }] : [];
    });
  }, [activeCategory, filteredPhrases]);

  const handleSelectPhrase = (messageKey: string): void => {
    requestPhraseInsertion(t(messageKey));
    hideDialog();
  };

  return (
    <Dialog open hideClose className={PREDEFINED_PHRASES_DIALOG_CLASSNAME}>
      <header className="flex h-10 shrink-0 items-center justify-between bg-plum-500 px-2 text-white">
        <div className="flex items-center gap-2">
          <TextInitial className="size-5 shrink-0" aria-hidden />
          <h2 className="text-base font-medium leading-5">{modalTitle}</h2>
        </div>
        <button
          type="button"
          aria-label={closeLabel}
          className="rounded-sm opacity-80 transition-opacity hover:opacity-100"
          onClick={hideDialog}
        >
          <X className="size-5" />
        </button>
      </header>
      <div className="flex min-h-0 flex-1 flex-col p-3">
        <div className="flex shrink-0 flex-col gap-3">
          <div className="relative">
            <Search
              className="pointer-events-none absolute top-1/2 left-2 size-4 -translate-y-1/2 text-plum-500"
              strokeWidth={1.25}
              aria-hidden
            />
            <input
              type="search"
              value={searchQuery}
              onChange={event => setSearchQuery(event.target.value)}
              placeholder={t('Chat.PredefinedPhrases.searchPlaceholder')}
              aria-label={t('Chat.PredefinedPhrases.searchPlaceholder')}
              className={SEARCH_INPUT_CLASSNAME}
            />
          </div>
          <div className="flex gap-2 overflow-x-auto">
            {PREDEFINED_PHRASE_CATEGORY_FILTERS.map(categoryId => (
              <button
                key={categoryId}
                type="button"
                aria-pressed={activeCategory === categoryId}
                onClick={() => setActiveCategory(categoryId)}
                className={categoryChipClassName(activeCategory === categoryId)}
              >
                {t(PREDEFINED_PHRASE_CATEGORY_LABEL_KEYS[categoryId])}
              </button>
            ))}
          </div>
        </div>
        <div className="predefined-phrases-scrollbar mt-4 flex min-h-0 min-w-0 flex-1 flex-col gap-4 overflow-x-hidden overflow-y-auto">
          {groupedPhrases.length === 0 ? (
            <p className="py-8 text-center text-sm text-gray-500">
              {t('Chat.PredefinedPhrases.empty')}
            </p>
          ) : (
            groupedPhrases.map(group => (
              <section key={group.categoryId} className="flex flex-col gap-2">
                <h3 className="text-sm font-medium leading-5 text-denim-800">
                  {withCount(
                    t(PREDEFINED_PHRASE_CATEGORY_LABEL_KEYS[group.categoryId]),
                    group.phrases.length
                  )}
                </h3>
                <ul className="flex flex-col gap-3 pl-5">
                  {group.phrases.map(phrase => (
                    <li key={phrase.id} className="group">
                      <button
                        type="button"
                        onClick={() => handleSelectPhrase(phrase.messageKey)}
                        className={phraseItemClassName}
                      >
                        <span className="min-w-0 flex-1 text-denim-600">
                          {t(phrase.messageKey)}
                        </span>
                        <ChevronRight
                          className="size-6 shrink-0 text-plum-400"
                          strokeWidth={1.25}
                          aria-hidden
                        />
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            ))
          )}
        </div>
      </div>
    </Dialog>
  );
};
